// SB6-ANA-POP-02 — the per-widget "Move to window" control (Christian Q1).
//
// Replaces the Phase-1 pop-out toggle. Behaviour by current location +
// window count (max 3):
//   - widget in MAIN, no windows open  → click opens a NEW window directly
//     (Christian: "if no window exists, create a new window") — no menu.
//   - widget in MAIN, ≥1 window open   → menu: each existing window + (if <3)
//     "New window".
//   - widget IN a window               → menu: "Bring back to dashboard" +
//     other windows + (if <3) "New window".
//
// Same `data-testid="popout-button-<widget>"` on the trigger so existing e2e
// keeps finding it. Snackbars reuse the Phase-1 copy for popup-blocked +
// non-Chromium advice.
import { useState } from "react";
import {
  Alert,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  usePopoutWorkspaceStore,
  windowOf,
  MAX_POPOUT_WINDOWS,
} from "./popoutWorkspaceStore.js";
import {
  usePopoutWidgetMove,
  type MoveDestination,
  type MoveResult,
} from "./usePopoutWidgetMove.js";
import {
  MIN_POPOUT_VIEWPORT_PX,
  WIDGET_LABEL,
  type PopoutWidgetKey,
} from "./windowSizes.js";
import { ExternalLinkIcon } from "@aeriosiq/ui/icons";

const POPUP_BLOCKED_COPY =
  "Your browser blocked the new window — click the popup icon in your " +
  "address bar to allow popups for this site, then try again.";
const ADVICE_COPY =
  "For the best multi-window experience, use Chrome or Edge: they can place " +
  "the window on your second monitor. It still works here in its own window.";

function windowSummary(widgets: readonly PopoutWidgetKey[]): string {
  return widgets.map((w) => WIDGET_LABEL[w]).join(", ");
}

export interface MoveWidgetControlProps {
  widget: PopoutWidgetKey;
}

export function MoveWidgetControl({
  widget,
}: MoveWidgetControlProps): React.ReactElement | null {
  const desktop = useMediaQuery(
    "(min-width:" + String(MIN_POPOUT_VIEWPORT_PX) + "px)",
  );
  const windows = usePopoutWorkspaceStore((s) => s.windows);
  const { move } = usePopoutWidgetMove();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [snack, setSnack] = useState<{
    severity: "warning" | "info";
    message: string;
  } | null>(null);

  if (!desktop) return null;

  const current = windowOf(windows, widget); // null = main dashboard
  const canOpenNew = windows.length < MAX_POPOUT_WINDOWS;
  const directNew = current === null && windows.length === 0;

  const handleResult = (r: MoveResult): void => {
    if (r.blocked)
      setSnack({ severity: "warning", message: POPUP_BLOCKED_COPY });
    else if (r.advise) setSnack({ severity: "info", message: ADVICE_COPY });
  };

  const doMove = (dest: MoveDestination): void => {
    setAnchor(null);
    handleResult(move(widget, dest));
  };

  const onTrigger = (e: React.MouseEvent<HTMLElement>): void => {
    if (directNew) {
      handleResult(move(widget, { kind: "new" }));
      return;
    }
    setAnchor(e.currentTarget);
  };

  const label =
    current === null
      ? "Move " + WIDGET_LABEL[widget] + " to a window"
      : "Move or bring back " + WIDGET_LABEL[widget];

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size="small"
          onClick={onTrigger}
          aria-label={label}
          aria-haspopup={directNew ? undefined : "menu"}
          data-testid={"popout-button-" + widget}
          sx={{ width: 24, height: 24 }}
        >
          <ExternalLinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
        data-testid={"move-menu-" + widget}
      >
        {current !== null && (
          <MenuItem
            onClick={() => {
              doMove({ kind: "main" });
            }}
            data-testid={"move-to-main-" + widget}
          >
            <ListItemText primary="Bring back to dashboard" />
          </MenuItem>
        )}
        {windows.map((w, idx) =>
          w.windowId === current ? null : (
            <MenuItem
              key={w.windowId}
              onClick={() => {
                doMove({ kind: "window", windowId: w.windowId });
              }}
              data-testid={"move-to-window-" + widget + "-" + String(idx)}
            >
              <ListItemText
                primary={"Window " + String(idx + 1)}
                secondary={windowSummary(
                  w.layout.map((c) => c.i as PopoutWidgetKey),
                )}
              />
            </MenuItem>
          ),
        )}
        {canOpenNew && (current !== null || windows.length > 0) && <Divider />}
        {canOpenNew && (
          <MenuItem
            onClick={() => {
              doMove({ kind: "new" });
            }}
            data-testid={"move-to-new-" + widget}
          >
            <Typography variant="body2">New window</Typography>
          </MenuItem>
        )}
      </Menu>
      <Snackbar
        open={snack !== null}
        autoHideDuration={6000}
        onClose={() => {
          setSnack(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => {
              setSnack(null);
            }}
            data-testid="popout-snackbar"
          >
            {snack.message}
          </Alert>
        ) : (
          <div />
        )}
      </Snackbar>
    </>
  );
}
